import React, { useState } from 'react';
import {
  DocPage, PageHeader, Section, SubSection, Prerequisites,
  Para, Callout,
} from '../components/DocBlocks';
import './MilestoneFee.css';

const TOC = [
  { id: 'overview',   label: 'Overview' },
  { id: 'anatomy',    label: 'Milestone Anatomy' },
  { id: 'lifecycle',  label: 'Lifecycle' },
  { id: 'billing',    label: 'Billing Impact' },
  { id: 'simulator',  label: 'Simulator' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d} ${months[parseInt(m, 10) - 1]} ${y}`;
}

function billingPeriod(iso) {
  if (!iso) return null;
  const [y, m] = iso.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m, 10) - 1]} ${y}`;
}

function periodKey(iso) {
  const [y, m] = iso.split('-');
  return `${y}-${m}`;
}

function fmtMoney(n) {
  return `$${n.toLocaleString()}`;
}

// ── Initial milestone definitions ─────────────────────────────────────────────

const MILESTONE_DEFS = [
  { id: 1, name: 'Project Kickoff & Planning', rate: 10000, defaultEnd: '2025-02-14' },
  { id: 2, name: 'Development & Integration',  rate: 25000, defaultEnd: '2025-03-31' },
  { id: 3, name: 'User Acceptance Testing',    rate: 15000, defaultEnd: '2025-05-09' },
  { id: 4, name: 'Go-Live & Handover',         rate: 5000,  defaultEnd: '2025-06-20' },
];

const CONTRACT_START = '2025-01-01';

function buildInitialMilestones() {
  return MILESTONE_DEFS.map((def, i) => ({
    ...def,
    status: i === 0 ? 'active' : 'pending',
    startDate: i === 0 ? CONTRACT_START : null,
    endDate: null,
  }));
}

// ── Simulator ─────────────────────────────────────────────────────────────────

function MilestoneSimulator() {
  const [milestones, setMilestones] = useState(buildInitialMilestones);
  // pendingEnd: { id, date }  — milestone currently being ended
  const [pendingEnd, setPendingEnd] = useState(null);

  function openEndForm(id) {
    const def = MILESTONE_DEFS.find((d) => d.id === id);
    setPendingEnd({ id, date: def.defaultEnd });
  }

  function confirmEnd() {
    if (!pendingEnd) return;
    const { id, date } = pendingEnd;
    setMilestones((prev) => {
      const next = prev.map((m) => {
        if (m.id === id) return { ...m, status: 'ended', endDate: date };
        if (m.id === id + 1) return { ...m, status: 'active', startDate: date };
        return m;
      });
      return next;
    });
    setPendingEnd(null);
  }

  function reset() {
    setMilestones(buildInitialMilestones());
    setPendingEnd(null);
  }

  // Build billing report: group ended milestones by billing period
  const billingReport = (() => {
    const periods = {};
    milestones.forEach((m) => {
      if (m.status !== 'ended' || !m.endDate) return;
      const key = periodKey(m.endDate);
      if (!periods[key]) periods[key] = { key, label: billingPeriod(m.endDate), lines: [], total: 0 };
      periods[key].lines.push({ name: m.name, endDate: m.endDate, rate: m.rate });
      periods[key].total += m.rate;
    });
    return Object.values(periods).sort((a, b) => a.key.localeCompare(b.key));
  })();

  const activeMs = milestones.find((m) => m.status === 'active');

  return (
    <div>
      <div className="mf-sim">
        <div className="mf-sim-header">
          <span className="mf-sim-title">Implementation Fee</span>
          <div className="mf-sim-meta">
            <span className="mf-sim-meta-item">Contract start <strong>{fmtDate(CONTRACT_START)}</strong></span>
            <span className="mf-sim-meta-item">Billing cycle <strong>Monthly</strong></span>
          </div>
        </div>

        <div className="mf-sim-milestones">
          {milestones.map((m) => {
            const isEnding = pendingEnd?.id === m.id;
            return (
              <div
                key={m.id}
                className={`mf-ms-row${m.status === 'active' ? ' mf-ms-row--active' : m.status === 'ended' ? ' mf-ms-row--ended' : ''}`}
              >
                {/* connector */}
                <div className="mf-ms-connector">
                  <div className={`mf-ms-dot mf-ms-dot--${m.status}`} />
                  <div className="mf-ms-line" />
                </div>

                {/* body */}
                <div className="mf-ms-body">
                  <span className="mf-ms-name">{m.name}</span>
                  <div className="mf-ms-dates">
                    <span>Start</span>
                    <span className="mf-ms-date-val">{m.startDate ? fmtDate(m.startDate) : '—'}</span>
                    <span className="mf-ms-date-sep">→</span>
                    <span>End</span>
                    <span className="mf-ms-date-val">{m.endDate ? fmtDate(m.endDate) : '—'}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="mf-ms-rate">{fmtMoney(m.rate)}</span>
                    {m.endDate && (
                      <span className="mf-ms-period-badge">
                        billed in {billingPeriod(m.endDate)}
                      </span>
                    )}
                  </div>
                </div>

                {/* action */}
                <div className="mf-ms-action">
                  {m.status === 'active' && !isEnding && (
                    <button className="mf-ms-end-btn" onClick={() => openEndForm(m.id)}>
                      End Milestone
                    </button>
                  )}
                  {m.status === 'active' && isEnding && (
                    <div className="mf-ms-end-form">
                      <span className="mf-ms-end-label">End date</span>
                      <input
                        type="date"
                        className="mf-ms-date-input"
                        value={pendingEnd.date}
                        min={m.startDate || CONTRACT_START}
                        onChange={(e) => setPendingEnd((p) => ({ ...p, date: e.target.value }))}
                      />
                      <button
                        className="mf-ms-confirm-btn"
                        onClick={confirmEnd}
                        disabled={!pendingEnd.date}
                      >
                        Confirm
                      </button>
                      <button className="mf-ms-cancel-link" onClick={() => setPendingEnd(null)}>
                        cancel
                      </button>
                    </div>
                  )}
                  {m.status === 'ended' && (
                    <span className="mf-ms-ended-badge">Ended</span>
                  )}
                  {m.status === 'pending' && (
                    <span className="mf-ms-pending-badge">Waiting</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Billing report */}
      <div className="mf-billing">
        <div className="mf-billing-header">
          <span className="mf-billing-title">Billing Report</span>
          <span className="mf-billing-subtitle">charges that appear in each monthly invoice</span>
        </div>
        {billingReport.length === 0 ? (
          <div className="mf-billing-empty">
            End a milestone above to see its charge appear here.
          </div>
        ) : (
          <div className="mf-billing-periods">
            {billingReport.map((period) => (
              <div key={period.key} className="mf-billing-period">
                <div className="mf-billing-period-header">
                  <span className="mf-billing-period-name">{period.label}</span>
                  <span className="mf-billing-period-total">{fmtMoney(period.total)}</span>
                </div>
                {period.lines.map((line) => (
                  <div key={line.name} className="mf-billing-line">
                    <div>
                      <div className="mf-billing-line-name">{line.name}</div>
                      <div className="mf-billing-line-note">ended {fmtDate(line.endDate)}</div>
                    </div>
                    <span className="mf-billing-line-amount">{fmtMoney(line.rate)}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {milestones.some((m) => m.status === 'ended') && (
        <button className="mf-reset-btn" onClick={reset}>
          Reset simulator
        </button>
      )}

      {activeMs && (
        <Callout type="info">
          <strong>{activeMs.name}</strong> is the active milestone.
          Only the current active milestone can be ended — milestones must be completed in order.
        </Callout>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MilestoneFee({ onNavigate }) {
  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Contracts · Fee Settings"
        title="Milestone-Based Fees"
        description="A milestone-based fee charges a fixed amount when a defined project milestone is manually completed — not based on usage. Each milestone has its own rate, and its charge lands in the billing period when it is ended."
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'contract-fee-components', label: 'Fee Settings' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          When you add a fee to a contract you choose its type: <strong>usage-based</strong> or
          {' '}<strong>milestone-based</strong>. Usage-based fees are driven by meter events —
          the charge is computed from what the customer actually consumed during the period.
          Milestone-based fees are independent of usage — each milestone carries a fixed amount
          that is invoiced only when that milestone is manually ended.
        </Para>

        <div className="mf-compare">
          <div className="mf-compare-card">
            <div className="mf-compare-header">
              <span className="mf-compare-icon">↗</span>
              <span className="mf-compare-title">Usage-based</span>
            </div>
            <div className="mf-compare-body">
              <ul>
                <li>Driven by meter events</li>
                <li>Rate card applies a pricing model to consumption</li>
                <li>Invoice generated at end of billing cycle</li>
                <li>Amount varies with usage</li>
              </ul>
            </div>
          </div>
          <div className="mf-compare-card mf-compare-card--active">
            <div className="mf-compare-header">
              <span className="mf-compare-icon">◈</span>
              <span className="mf-compare-title">Milestone-based</span>
            </div>
            <div className="mf-compare-body">
              <ul>
                <li>Driven by manual completion</li>
                <li>Each milestone has a fixed fee as its rate</li>
                <li>Charge lands in the period of the end date</li>
                <li>Amount is fixed at contract time</li>
              </ul>
            </div>
          </div>
        </div>

        <Para>
          Milestone-based fees are common for professional services, implementation projects,
          and staged deliverables where the customer is charged upon completion of defined phases —
          not on ongoing consumption.
        </Para>
      </Section>

      <Section id="anatomy" title="Milestone Anatomy">
        <Para>
          A milestone-based fee carries one or more milestones. Each milestone defines exactly
          what gets billed and when.
        </Para>

        <div className="mf-anatomy">
          <div className="mf-anatomy-header">
            <span className="mf-anatomy-title">Example milestone</span>
            <span className="mf-anatomy-status mf-anatomy-status--active">Active</span>
          </div>
          <div className="mf-anatomy-row">
            <span className="mf-anatomy-label">Name</span>
            <span className="mf-anatomy-value">Development &amp; Integration</span>
          </div>
          <div className="mf-anatomy-row">
            <span className="mf-anatomy-label">Rate</span>
            <span className="mf-anatomy-value">$25,000 (fixed)</span>
          </div>
          <div className="mf-anatomy-row">
            <span className="mf-anatomy-label">Start date</span>
            <span className="mf-anatomy-value">14 Feb 2025 — set when the previous milestone ended</span>
          </div>
          <div className="mf-anatomy-row">
            <span className="mf-anatomy-label">End date</span>
            <span className="mf-anatomy-value">Not yet set — must be ended manually</span>
          </div>
        </div>

        <table className="ctr-table">
          <thead>
            <tr><th>Field</th><th>Description</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Name</td>
              <td>Label for this phase of work, shown on the invoice line item.</td>
            </tr>
            <tr>
              <td>Rate</td>
              <td>Fixed amount charged when this milestone is ended. Set at contract creation time.</td>
            </tr>
            <tr>
              <td>Start date</td>
              <td>
                For Milestone 1, this is the contract start date.
                For Milestone N, it is automatically set to the end date of Milestone N−1.
              </td>
            </tr>
            <tr>
              <td>End date</td>
              <td>
                Set manually when work is complete. The billing period is determined by this date.
                A milestone is not billed until it is explicitly ended.
              </td>
            </tr>
          </tbody>
        </table>

        <Callout type="info" title="Milestones are sequential">
          Milestone 2 can only be ended after Milestone 1 is ended. The start date of each milestone
          is set automatically to the end date of the one before it, creating an unbroken chain.
        </Callout>
      </Section>

      <Section id="lifecycle" title="Lifecycle">
        <Para>
          Each milestone moves through three states. At any point in the contract there is
          exactly one <strong>Active</strong> milestone — the one awaiting completion.
        </Para>

        <div className="mf-lifecycle">
          <div className="mf-lifecycle-step">
            <div className="mf-lifecycle-dot">—</div>
            <span className="mf-lifecycle-label">Pending</span>
            <span className="mf-lifecycle-note">Not yet started</span>
          </div>
          <span className="mf-lifecycle-arrow">→</span>
          <div className="mf-lifecycle-step">
            <div className="mf-lifecycle-dot mf-lifecycle-dot--active">●</div>
            <span className="mf-lifecycle-label">Active</span>
            <span className="mf-lifecycle-note">In progress</span>
          </div>
          <span className="mf-lifecycle-arrow">→</span>
          <div className="mf-lifecycle-step">
            <div className="mf-lifecycle-dot mf-lifecycle-dot--ended">✓</div>
            <span className="mf-lifecycle-label">Ended</span>
            <span className="mf-lifecycle-note">Charge locked</span>
          </div>
        </div>

        <table className="ctr-table">
          <thead>
            <tr><th>State</th><th>Meaning</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>Pending</td>
              <td>The milestone has not started yet. A preceding milestone must be ended first.</td>
            </tr>
            <tr>
              <td>Active</td>
              <td>
                The milestone is in progress. Its start date is set. The fee has not yet been invoiced.
                This is the only state from which a milestone can be ended.
              </td>
            </tr>
            <tr>
              <td>Ended</td>
              <td>
                The milestone is complete. The end date is recorded. The charge will appear in the
                billing report for the period that end date falls in. The next milestone becomes Active.
              </td>
            </tr>
          </tbody>
        </table>
      </Section>

      <Section id="billing" title="Billing Impact">
        <Para>
          When a milestone is ended, its fixed rate is added to the invoice for the billing period
          in which the end date falls. The billing cycle of the fee (monthly, quarterly, annual)
          determines which invoice picks it up.
        </Para>

        <SubSection title="How the charge is placed">
          <div className="mf-seq">
            <div className="mf-seq-step">
              <span className="mf-seq-num">1</span>
              <span className="mf-seq-text">User ends a milestone and records an end date — for example, 14 Feb 2025.</span>
            </div>
            <div className="mf-seq-arrow">↓</div>
            <div className="mf-seq-step">
              <span className="mf-seq-num">2</span>
              <span className="mf-seq-text">The system identifies the billing period that contains 14 Feb 2025 — in this case the February 2025 invoice.</span>
            </div>
            <div className="mf-seq-arrow">↓</div>
            <div className="mf-seq-step">
              <span className="mf-seq-num">3</span>
              <span className="mf-seq-text">The milestone's fixed rate is added as a line item on that invoice. Taxes and contract discounts apply in the normal charge sequence.</span>
            </div>
          </div>

          <Callout type="tip" title="End date controls the period">
            If a milestone's work actually finished in March but you record the end date in April,
            the charge falls in April's invoice. The end date is the authoritative timestamp —
            choose it to match when you consider the milestone accepted.
          </Callout>
        </SubSection>

        <SubSection title="Multiple milestones ending in the same period">
          <Para>
            If two milestones are ended in the same calendar month, both charges appear on that
            month's invoice as separate line items — one per milestone. Each line shows the
            milestone name and its fixed rate.
          </Para>
        </SubSection>
      </Section>

      <Section id="simulator" title="Simulator">
        <Para>
          This contract has a single milestone-based fee called <strong>Implementation Fee</strong>.
          It has four milestones — each must be ended manually to unlock the next one and generate
          the invoice charge. Pick an end date and confirm to see how the billing report updates.
        </Para>

        <MilestoneSimulator />
      </Section>

    </DocPage>
  );
}
